/**
 * Data Controller Dropdown — cascading menu for assigning data controllers
 * to project items. Matches the Titler Live UI pattern:
 *   None | API ► | Gaming ► | General ► | Productivity ► | Social ► | Sports ► | Worship ►
 *   Sports > ... > Scorebird > [sport] > New Input
 *
 * For the simulation, only Sports > Scorebird is functional.
 * Other categories are stubs (shown but disabled).
 */
import { disconnectScorebird, getConnection } from '../engine/scorebirdPoller';
import { eventBus } from '../engine/eventBus';

// ── Pending sport selection (before NeST ID entered) ─────────
// Maps projectItemId → selected sport string
const pendingSport = new Map<string, string>();

export function getPendingSport(projectItemId: string): string | undefined {
  return pendingSport.get(projectItemId);
}

export function clearPendingSport(projectItemId: string): void {
  pendingSport.delete(projectItemId);
}

// ── Menu data ────────────────────────────────────────────────

interface MenuItem {
  label: string;
  hasSubmenu?: boolean;
  disabled?: boolean;
  action?: () => void;
  children?: MenuItem[];
}

const SCOREBIRD_SPORTS: Array<{ label: string; value: string }> = [
  { label: 'Baseball/Softball', value: 'baseball' },
  { label: 'Basketball', value: 'basketball' },
  { label: 'Football', value: 'football' },
  { label: 'Hockey', value: 'hockey' },
  { label: 'Lacrosse', value: 'lacrosse' },
  { label: 'Scores Only', value: 'scores_only' },
  { label: 'Soccer', value: 'soccer' },
  { label: 'Swimming', value: 'swimming' },
  { label: 'Volleyball', value: 'volleyball' },
  { label: 'Wrestling', value: 'wrestling' },
];

const SPORTS_PROVIDERS: MenuItem[] = [
  { label: 'FinishLynx', hasSubmenu: true, disabled: true },
  { label: 'Hardware Scoreboard', hasSubmenu: true, disabled: true },
  { label: 'JSON', hasSubmenu: true, disabled: true },
  { label: 'Leaderboard', hasSubmenu: true, disabled: true },
  { label: 'NewTek DataLink', hasSubmenu: true, disabled: true },
  { label: 'RSS Reader', hasSubmenu: true, disabled: true },
  { label: 'Roster', hasSubmenu: true, disabled: true },
  { label: 'ScoreBuddy', hasSubmenu: true, disabled: true },
  { label: 'Scorebird', hasSubmenu: true },  // functional
  { label: 'Scoreboard Tool', hasSubmenu: true, disabled: true },
  { label: 'Sportzcast', hasSubmenu: true, disabled: true },
  { label: 'Spreadsheet', hasSubmenu: true, disabled: true },
  { label: 'Stat Crew Scoreboards', hasSubmenu: true, disabled: true },
  { label: 'Stat Crew Scoreboards 2', hasSubmenu: true, disabled: true },
  { label: 'Stat Crew Stats', hasSubmenu: true, disabled: true },
  { label: 'Stat Crew Stats 2', hasSubmenu: true, disabled: true },
  { label: 'XML', hasSubmenu: true, disabled: true },
];

function buildTopMenu(projectItemId: string): MenuItem[] {
  return [
    { label: 'None', action: () => disconnectScorebird(projectItemId) },
    { label: 'API', hasSubmenu: true, disabled: true },
    { label: 'Gaming', hasSubmenu: true, disabled: true },
    { label: 'General', hasSubmenu: true, disabled: true },
    { label: 'Productivity', hasSubmenu: true, disabled: true },
    { label: 'Social', hasSubmenu: true, disabled: true },
    { label: 'Sports', hasSubmenu: true, children: SPORTS_PROVIDERS },
    { label: 'Worship', hasSubmenu: true, disabled: true },
  ];
}

// ── Rendering ────────────────────────────────────────────────

let activeOverlay: HTMLElement | null = null;
let activeItemId: string | null = null;

/** Close any open data controller menu */
export function closeDataControllerMenu(): void {
  if (activeOverlay) {
    activeOverlay.remove();
    activeOverlay = null;
    activeItemId = null;
  }
}

/**
 * Open the Data Controller dropdown for a project item.
 * @param projectItemId  The project item to attach the controller to.
 * @param anchorEl       The DOM element to anchor the dropdown near.
 */
export function openDataControllerMenu(projectItemId: string, anchorEl: HTMLElement): void {
  // Close existing
  closeDataControllerMenu();
  activeItemId = projectItemId;

  // Build overlay
  activeOverlay = document.createElement('div');
  activeOverlay.className = 'dc-overlay';
  activeOverlay.addEventListener('click', (e) => {
    if (e.target === activeOverlay) closeDataControllerMenu();
  });

  // Render top-level menu
  const menu = buildTopMenu(projectItemId);
  const menuEl = renderMenu(menu, projectItemId);

  // Position near anchor
  const rect = anchorEl.getBoundingClientRect();
  menuEl.style.left = `${rect.left}px`;
  menuEl.style.top = `${rect.bottom + 2}px`;

  activeOverlay.appendChild(menuEl);
  document.body.appendChild(activeOverlay);
}

function renderMenu(items: MenuItem[], projectItemId: string): HTMLElement {
  const ul = document.createElement('ul');
  ul.className = 'dc-menu';

  for (const item of items) {
    const li = document.createElement('li');
    li.className = `dc-item${item.disabled ? ' dc-disabled' : ''}${item.hasSubmenu ? ' dc-has-submenu' : ''}`;
    li.innerHTML = `<span class="dc-label">${item.label}</span>${item.hasSubmenu ? '<span class="dc-arrow">&#x25B6;</span>' : ''}`;

    if (item.action && !item.disabled) {
      li.addEventListener('click', (e) => {
        e.stopPropagation();
        item.action!();
        closeDataControllerMenu();
        // Re-render the project panel to update the Data Controller column
        import('../ui/project').then(m => m.renderProjectList?.());
      });
    }

    // Submenu handling
    if (item.hasSubmenu && !item.disabled) {
      let submenuEl: HTMLElement | null = null;

      li.addEventListener('mouseenter', () => {
        // Close sibling submenus
        ul.querySelectorAll(':scope > li > .dc-menu').forEach((el) => el.remove());

        if (item.label === 'Scorebird') {
          submenuEl = renderScorebirdSportsMenu(projectItemId);
        } else if (item.children) {
          submenuEl = renderMenu(item.children, projectItemId);
        }

        if (submenuEl) {
          submenuEl.classList.add('dc-submenu');
          li.appendChild(submenuEl);
          positionSubmenu(li, submenuEl);
        }
      });
    }

    ul.appendChild(li);
  }

  return ul;
}

function renderScorebirdSportsMenu(projectItemId: string): HTMLElement {
  const ul = document.createElement('ul');
  ul.className = 'dc-menu';

  for (const sport of SCOREBIRD_SPORTS) {
    const li = document.createElement('li');
    li.className = 'dc-item dc-has-submenu';
    li.innerHTML = `<span class="dc-label">${sport.label}</span><span class="dc-arrow">&#x25B6;</span>`;

    li.addEventListener('mouseenter', () => {
      ul.querySelectorAll(':scope > li > .dc-menu').forEach((el) => el.remove());
      const sub = renderNewInputMenu(projectItemId, sport.value);
      sub.classList.add('dc-submenu');
      li.appendChild(sub);
      positionSubmenu(li, sub);
    });

    ul.appendChild(li);
  }

  return ul;
}

function renderNewInputMenu(projectItemId: string, sport: string): HTMLElement {
  const ul = document.createElement('ul');
  ul.className = 'dc-menu';

  const li = document.createElement('li');
  li.className = 'dc-item';
  li.innerHTML = '<span class="dc-label">New Input</span>';
  li.addEventListener('click', (e) => {
    e.stopPropagation();
    // Store the selected sport and open the ScoreBird Settings tab in Properties
    pendingSport.set(projectItemId, sport);
    closeDataControllerMenu();
    eventBus.emit('data:newInputRequested', { projectItemId, sport });
    // Switch Properties panel to ScoreBird tab
    import('../ui/properties').then(m => m.showScorebirdSettings?.());
    // Re-render project list
    import('../ui/project').then(m => m.renderProjectList?.());
  });

  ul.appendChild(li);
  return ul;
}

function positionSubmenu(parentLi: HTMLElement, submenu: HTMLElement): void {
  // Position submenu to the right of the parent item
  requestAnimationFrame(() => {
    const parentRect = parentLi.getBoundingClientRect();
    const menuRect = submenu.getBoundingClientRect();

    // Check if submenu would go off-screen right
    if (parentRect.right + menuRect.width > window.innerWidth) {
      submenu.style.left = 'auto';
      submenu.style.right = '100%';
    }
    // Check if submenu would go off-screen bottom
    if (parentRect.top + menuRect.height > window.innerHeight) {
      submenu.style.top = 'auto';
      submenu.style.bottom = '0';
    }
  });
}

/**
 * Get display text for the Data Controller column.
 */
export function getDataControllerLabel(projectItemId: string): string {
  const conn = getConnection(projectItemId);
  if (conn) {
    return `<span class="dc-active" title="ScoreBird #${conn.nestId} (${conn.sport})">Scorebird</span>`;
  }
  const pending = pendingSport.get(projectItemId);
  if (pending) {
    return `<span class="dc-pending" title="ScoreBird (${pending}) — not connected">Scorebird</span>`;
  }
  return '';
}
